import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import scopeAIService from "../../services/scopeAIService";
import { classifyWorkflowError } from "../../lib/workflowErrors";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Loader2,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Home,
  Wrench,
  AlertTriangle,
  Calendar,
  CheckCircle,
  CloudOff,
  Check,
} from "lucide-react";

const STEPS = [
  { id: "dimensions", title: "Dimensions", icon: Home },
  { id: "systems", title: "Systems", icon: Wrench },
  { id: "requirements", title: "Requirements", icon: AlertTriangle },
  { id: "timeline", title: "Timeline", icon: Calendar },
  { id: "questions", title: "AI Questions", icon: Sparkles },
  { id: "review", title: "Review", icon: CheckCircle },
];

const ScopeIntakeWizard = ({ project, isOpen, onClose, onPlanGenerated }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [scheduleStartDate, setScheduleStartDate] = useState("");
  const [applying, setApplying] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  
  // AI Questions state
  const [aiQuestions, setAiQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [analysisummary, setAnalysisSummary] = useState("");
  
  // Default form values
  const defaultFormData = {
    // Dimensions
    square_footage: "",
    num_stories: 1,
    num_units: 1,
    bathrooms: 0,
    kitchens: 0,
    bedrooms: 0,
    living_areas: 0,
    garage_spaces: 0,
    has_basement: false,
    has_attic: false,
    
    // Systems
    electrical_new_service: false,
    electrical_panel_upgrade: false,
    electrical_rewire: false,
    plumbing_new_service: false,
    plumbing_repipe: false,
    plumbing_fixture_replacement: false,
    hvac_new_system: false,
    hvac_replacement: false,
    hvac_ductwork: false,
    structural_modifications: false,
    roofing: false,
    windows_replacement: false,
    doors_replacement: false,
    insulation: false,
    waterproofing: false,
    
    // Scope narrative
    scope_narrative: "",
    
    // Constraints
    budget_min: "",
    budget_max: "",
    target_start_date: "",
    target_end_date: "",
    
    // Special requirements
    ada_compliance: false,
    historic_preservation: false,
    leed_certification: false,
    energy_star: false,
    fire_sprinkler_required: false,
    seismic_retrofit: false,
    hazmat_abatement: false,
    occupied_during_work: false,
    
    notes: "",
    
    // Discovery answers (from AI questions)
    discovery_answers: {},
  };

  // Form state
  const [formData, setFormData] = useState(defaultFormData);
  
  // Load saved draft from backend when wizard opens
  useEffect(() => {
    const loadDraft = async () => {
      if (!isOpen || !project?.id) return;
      
      setLoadingDraft(true);
      try {
        const result = await scopeAIService.getDraft(project.id);
        if (result.exists && result.draft) {
          const draft = typeof result.draft === 'string' ? JSON.parse(result.draft) : result.draft;
          if (draft.formData) {
            setFormData({ ...defaultFormData, ...draft.formData });
          }
          if (draft.currentStep !== undefined) {
            setCurrentStep(draft.currentStep);
          }
          toast.success("Previous progress restored");
        }
      } catch (error) {
        console.error('Failed to load draft:', error);
      } finally {
        setLoadingDraft(false);
      }
    };
    
    loadDraft();
  }, [isOpen, project?.id]);
  
  // Debounced save to backend
  const saveTimeoutRef = useRef(null);
  
  const saveDraft = useCallback(async (data, step) => {
    if (!project?.id) return;
    
    setSaveStatus('saving');
    try {
      await scopeAIService.saveDraft(project.id, {
        formData: data,
        currentStep: step,
        savedAt: new Date().toISOString()
      });
      setSaveStatus('saved');
      // Clear status after 2 seconds
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Failed to save draft:', error);
      setSaveStatus('error');
    }
  }, [project?.id]);
  
  // Auto-save when form data changes (debounced)
  useEffect(() => {
    if (loadingDraft || !project?.id) return;
    
    // Clear previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout to save after 1 second of inactivity
    saveTimeoutRef.current = setTimeout(() => {
      saveDraft(formData, currentStep);
    }, 1000);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, currentStep, loadingDraft, project?.id, saveDraft]);
  
  // Clear draft after successful plan application
  const clearDraft = async () => {
    if (!project?.id) return;
    try {
      await scopeAIService.clearDraft(project.id);
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!formData.scope_narrative || formData.scope_narrative.length < 10) {
      toast.error("Please provide a detailed scope description");
      return;
    }

    setGenerating(true);
    try {
      const intake = {
        project_id: project.id,
        ...formData,
        square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
        budget_min: formData.budget_min || null,
        budget_max: formData.budget_max || null,
        target_start_date: formData.target_start_date || null,
        target_end_date: formData.target_end_date || null,
      };

      console.log("Sending intake:", intake);
      const plan = await scopeAIService.generatePlan(intake);
      setGeneratedPlan(plan);
      setScheduleStartDate(formData.target_start_date || "");
      toast.success("Project plan generated!");
    } catch (error) {
      const message = classifyWorkflowError(error).message;
      toast.error(message);
      console.error("Generation error:", error);
    } finally {
      setGenerating(false);
    }
  };

  const handleApplyPlan = async () => {
    if (!generatedPlan || !scheduleStartDate || applying) return;

    setApplying(true);
    try {
      await scopeAIService.applyPlan(project.id, { ...generatedPlan, schedule_start_date: scheduleStartDate });
      await clearDraft(); // Clear the saved draft after successful application
      toast.success("Plan applied to project!");
      onPlanGenerated?.(generatedPlan);
      onClose();
    } catch (error) {
      toast.error(classifyWorkflowError(error).message);
    } finally {
      setApplying(false);
    }
  };

  // Fetch AI questions when entering the questions step
  const fetchAIQuestions = async () => {
    if (aiQuestions.length > 0) return; // Already fetched
    
    setLoadingQuestions(true);
    try {
      const intakeData = buildIntakePayload();
      const response = await scopeAIService.analyzeScope(intakeData);
      setAiQuestions(response.questions || []);
      setAnalysisSummary(response.analysis_summary || "");
    } catch (error) {
      console.error("Failed to fetch AI questions:", error);
      toast.error("Could not load AI questions. You can skip this step.");
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Build intake payload from form data
  const buildIntakePayload = () => ({
    project_id: project.id,
    scope_narrative: formData.scope_narrative,
    square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
    num_stories: formData.num_stories,
    num_units: formData.num_units,
    bathrooms: formData.bathrooms,
    kitchens: formData.kitchens,
    bedrooms: formData.bedrooms,
    living_areas: formData.living_areas,
    garage_spaces: formData.garage_spaces,
    has_basement: formData.has_basement,
    has_attic: formData.has_attic,
    electrical_new_service: formData.electrical_new_service,
    electrical_panel_upgrade: formData.electrical_panel_upgrade,
    electrical_rewire: formData.electrical_rewire,
    plumbing_new_service: formData.plumbing_new_service,
    plumbing_repipe: formData.plumbing_repipe,
    plumbing_fixture_replacement: formData.plumbing_fixture_replacement,
    hvac_new_system: formData.hvac_new_system,
    hvac_replacement: formData.hvac_replacement,
    hvac_ductwork: formData.hvac_ductwork,
    structural_modifications: formData.structural_modifications,
    roofing: formData.roofing,
    windows_replacement: formData.windows_replacement,
    doors_replacement: formData.doors_replacement,
    insulation: formData.insulation,
    waterproofing: formData.waterproofing,
    budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
    budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
    target_start_date: formData.target_start_date || "",
    target_end_date: formData.target_end_date || "",
    ada_compliance: formData.ada_compliance,
    historic_preservation: formData.historic_preservation,
    leed_certification: formData.leed_certification,
    energy_star: formData.energy_star,
    fire_sprinkler_required: formData.fire_sprinkler_required,
    seismic_retrofit: formData.seismic_retrofit,
    hazmat_abatement: formData.hazmat_abatement,
    occupied_during_work: formData.occupied_during_work,
    notes: formData.notes,
    discovery_answers: Object.entries(formData.discovery_answers).map(([id, answer]) => ({
      question_id: id,
      answer: answer
    }))
  });

  const nextStep = () => {
    // Validate requirements step - scope narrative is required
    if (STEPS[currentStep].id === "requirements" && (!formData.scope_narrative || formData.scope_narrative.length < 10)) {
      toast.error("Please enter a detailed scope description (at least 10 characters)");
      return;
    }
    
    // Trigger question fetch when entering questions step
    if (STEPS[currentStep].id === "timeline") {
      fetchAIQuestions();
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Update discovery answer
  const updateDiscoveryAnswer = (questionId, answer) => {
    setFormData(prev => ({
      ...prev,
      discovery_answers: {
        ...prev.discovery_answers,
        [questionId]: answer
      }
    }));
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const renderStepContent = () => {
    switch (STEPS[currentStep].id) {
      case "dimensions":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Project Dimensions</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Square Footage</Label>
                  <Input
                    type="number"
                    value={formData.square_footage}
                    onChange={(e) => updateField("square_footage", e.target.value)}
                    placeholder="e.g., 2500"
                    className="bg-muted/50 mt-1"
                  />
                </div>
                <div>
                  <Label>Number of Stories</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.num_stories}
                    onChange={(e) => updateField("num_stories", parseInt(e.target.value) || 1)}
                    className="bg-muted/50 mt-1"
                  />
                </div>
              </div>
            </div>

            {project?.property_class === "residential" && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Room Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Bathrooms</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.bathrooms}
                      onChange={(e) => updateField("bathrooms", parseInt(e.target.value) || 0)}
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label>Kitchens</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.kitchens}
                      onChange={(e) => updateField("kitchens", parseInt(e.target.value) || 0)}
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label>Bedrooms</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.bedrooms}
                      onChange={(e) => updateField("bedrooms", parseInt(e.target.value) || 0)}
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label>Living Areas</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.living_areas}
                      onChange={(e) => updateField("living_areas", parseInt(e.target.value) || 0)}
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label>Garage Spaces</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.garage_spaces}
                      onChange={(e) => updateField("garage_spaces", parseInt(e.target.value) || 0)}
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                </div>
                <div className="flex gap-6 mt-4">
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.has_basement}
                      onCheckedChange={(v) => updateField("has_basement", v)}
                    />
                    <span>Has Basement</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.has_attic}
                      onCheckedChange={(v) => updateField("has_attic", v)}
                    />
                    <span>Has Attic</span>
                  </label>
                </div>
              </div>
            )}

            {project?.property_class === "commercial" && (
              <div>
                <Label>Number of Units/Suites</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.num_units}
                  onChange={(e) => updateField("num_units", parseInt(e.target.value) || 1)}
                  className="bg-muted/50 mt-1 max-w-xs"
                />
              </div>
            )}
          </div>
        );

      case "systems":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">Systems Scope</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Select all systems work included in this project
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              {/* Electrical */}
              <Card className="bg-muted/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Electrical</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { key: "electrical_new_service", label: "New Service" },
                    { key: "electrical_panel_upgrade", label: "Panel Upgrade" },
                    { key: "electrical_rewire", label: "Rewiring" },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2">
                      <Checkbox
                        checked={formData[key]}
                        onCheckedChange={(v) => updateField(key, v)}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </CardContent>
              </Card>

              {/* Plumbing */}
              <Card className="bg-muted/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Plumbing</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { key: "plumbing_new_service", label: "New Service" },
                    { key: "plumbing_repipe", label: "Repiping" },
                    { key: "plumbing_fixture_replacement", label: "Fixture Replacement" },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2">
                      <Checkbox
                        checked={formData[key]}
                        onCheckedChange={(v) => updateField(key, v)}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </CardContent>
              </Card>

              {/* HVAC */}
              <Card className="bg-muted/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">HVAC</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { key: "hvac_new_system", label: "New System" },
                    { key: "hvac_replacement", label: "Replacement" },
                    { key: "hvac_ductwork", label: "Ductwork" },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2">
                      <Checkbox
                        checked={formData[key]}
                        onCheckedChange={(v) => updateField(key, v)}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </CardContent>
              </Card>

              {/* Other */}
              <Card className="bg-muted/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Other</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[
                    { key: "structural_modifications", label: "Structural Work" },
                    { key: "roofing", label: "Roofing" },
                    { key: "windows_replacement", label: "Windows" },
                    { key: "doors_replacement", label: "Doors" },
                    { key: "insulation", label: "Insulation" },
                    { key: "waterproofing", label: "Waterproofing" },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2">
                      <Checkbox
                        checked={formData[key]}
                        onCheckedChange={(v) => updateField(key, v)}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case "requirements":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Special Requirements</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: "ada_compliance", label: "ADA Compliance Required" },
                  { key: "historic_preservation", label: "Historic Preservation" },
                  { key: "leed_certification", label: "LEED Certification" },
                  { key: "energy_star", label: "Energy Star" },
                  { key: "fire_sprinkler_required", label: "Fire Sprinkler Required" },
                  { key: "seismic_retrofit", label: "Seismic Retrofit" },
                  { key: "hazmat_abatement", label: "Hazmat Abatement" },
                  { key: "occupied_during_work", label: "Occupied During Work" },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                    <Checkbox
                      checked={formData[key]}
                      onCheckedChange={(v) => updateField(key, v)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="scope_narrative">
                Scope Description *
                <span className="text-muted-foreground text-sm ml-2">(Be detailed - AI uses this)</span>
              </Label>
              <Textarea
                id="scope_narrative"
                name="scope_narrative"
                value={formData.scope_narrative}
                onChange={(e) => updateField("scope_narrative", e.target.value)}
                placeholder="Describe the work in detail. For example: 'Complete master bathroom remodel including removal of existing tub and installation of walk-in shower with frameless glass enclosure, new double vanity with quartz countertop, relocate toilet to opposite wall, add recessed lighting and heated floors...'"
                className="bg-muted/50 mt-1 min-h-[150px]"
              />
              {formData.scope_narrative && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.scope_narrative.length} characters entered
                </p>
              )}
            </div>
          </div>
        );

      case "timeline":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">Budget & Timeline</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Minimum Budget ($)</Label>
                <Input
                  type="number"
                  value={formData.budget_min}
                  onChange={(e) => updateField("budget_min", e.target.value)}
                  placeholder="e.g., 50000"
                  className="bg-muted/50 mt-1"
                />
              </div>
              <div>
                <Label>Maximum Budget ($)</Label>
                <Input
                  type="number"
                  value={formData.budget_max}
                  onChange={(e) => updateField("budget_max", e.target.value)}
                  placeholder="e.g., 75000"
                  className="bg-muted/50 mt-1"
                />
              </div>
              <div>
                <Label>Target Start Date</Label>
                <Input
                  type="date"
                  value={formData.target_start_date}
                  onChange={(e) => updateField("target_start_date", e.target.value)}
                  className="bg-muted/50 mt-1"
                />
              </div>
              <div>
                <Label>Target End Date</Label>
                <Input
                  type="date"
                  value={formData.target_end_date}
                  onChange={(e) => updateField("target_end_date", e.target.value)}
                  className="bg-muted/50 mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Any other information that might be helpful..."
                className="bg-muted/50 mt-1 min-h-[80px]"
              />
            </div>
          </div>
        );

      case "questions":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">AI Clarifying Questions</h3>
              <p className="text-sm text-muted-foreground">
                The AI has reviewed your project details and has a few questions to help generate a more accurate plan.
                These are optional—you can skip any or all of them.
              </p>
            </div>
            
            {loadingQuestions ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">AI is reviewing your project...</p>
              </div>
            ) : aiQuestions.length > 0 ? (
              <div className="space-y-4">
                {analysisummary && (
                  <Card className="bg-primary/10 border-primary/30">
                    <CardContent className="p-4">
                      <p className="text-sm"><strong>AI Understanding:</strong> {analysisummary}</p>
                    </CardContent>
                  </Card>
                )}
                
                {aiQuestions.map((q, index) => (
                  <Card key={q.id} className="bg-muted/20">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <span className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium">{q.question}</p>
                          {q.why_asking && (
                            <p className="text-xs text-muted-foreground mt-1">{q.why_asking}</p>
                          )}
                        </div>
                      </div>
                      
                      {q.type === "yes_no" ? (
                        <div className="flex gap-2 ml-9">
                          {["Yes", "No", "Unsure"].map((opt) => (
                            <Button
                              key={opt}
                              type="button"
                              variant={formData.discovery_answers[q.id] === opt ? "default" : "outline"}
                              size="sm"
                              onClick={() => updateDiscoveryAnswer(q.id, opt)}
                            >
                              {opt}
                            </Button>
                          ))}
                        </div>
                      ) : q.type === "multiple_choice" && q.options ? (
                        <div className="flex flex-wrap gap-2 ml-9">
                          {q.options.map((opt) => (
                            <Button
                              key={opt}
                              type="button"
                              variant={formData.discovery_answers[q.id] === opt ? "default" : "outline"}
                              size="sm"
                              onClick={() => updateDiscoveryAnswer(q.id, opt)}
                            >
                              {opt}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <div className="ml-9">
                          <Input
                            value={formData.discovery_answers[q.id] || ""}
                            onChange={(e) => updateDiscoveryAnswer(q.id, e.target.value)}
                            placeholder="Type your answer..."
                            className="bg-muted/50"
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                
                <p className="text-xs text-muted-foreground text-center">
                  Answering these questions is optional. Click "Next" to proceed to plan generation.
                </p>
              </div>
            ) : (
              <Card className="bg-muted/20">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p className="font-medium">No additional questions needed!</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Your project details are comprehensive enough. Click "Next" to proceed.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case "review":
        return (
          <div className="space-y-6">
            {!generatedPlan ? (
              <>
                <h3 className="text-lg font-semibold mb-4">Review & Generate</h3>
                
                {/* Project Info from Part 1 */}
                <Card className="bg-primary/10 border-primary/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-primary">Project Info (from creation)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <p><strong>Name:</strong> {project?.name}</p>
                    <p><strong>Type:</strong> {project?.property_class?.replace(/_/g, ' ')} - {project?.project_kind?.replace(/_/g, ' ')}</p>
                    <p><strong>Address:</strong> {project?.formatted_address || `${project?.city}, ${project?.state}`}</p>
                    {project?.budget && <p><strong>Budget:</strong> ${project?.budget?.toLocaleString()}</p>}
                    {project?.work_types?.length > 0 && (
                      <p><strong>Work Types:</strong> {project?.work_types?.map(wt => wt.name || wt.code).join(', ')}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Scope Intake Info */}
                <Card className="bg-muted/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Scope Details (from this wizard)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    {formData.square_footage && <p><strong>Size:</strong> {formData.square_footage} sq ft</p>}
                    {formData.num_stories > 1 && <p><strong>Stories:</strong> {formData.num_stories}</p>}
                    {(formData.bathrooms > 0 || formData.kitchens > 0 || formData.bedrooms > 0) && (
                      <p><strong>Rooms:</strong> {[
                        formData.bathrooms > 0 && `${formData.bathrooms} bath`,
                        formData.kitchens > 0 && `${formData.kitchens} kitchen`,
                        formData.bedrooms > 0 && `${formData.bedrooms} bed`,
                      ].filter(Boolean).join(', ')}</p>
                    )}
                    {formData.scope_narrative && (
                      <div className="pt-2">
                        <strong>Scope Description:</strong>
                        <p className="text-muted-foreground mt-1 whitespace-pre-wrap">{formData.scope_narrative}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Warning if scope narrative is missing */}
                {!formData.scope_narrative && (
                  <Card className="bg-red-500/10 border-red-500/30 mb-4">
                    <CardContent className="p-4 flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                      <div>
                        <p className="text-red-400 font-medium">Scope Description Required</p>
                        <p className="text-sm text-muted-foreground">
                          Please go back to the <strong>Requirements</strong> step and enter a detailed scope description. 
                          The AI needs this to generate your project plan.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {generating && (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                    <p className="text-lg font-medium">Generating your project plan...</p>
                    <p className="text-sm text-muted-foreground">This typically takes 30-60 seconds</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Plan Generated!
                </h3>
                
                <Card className="bg-muted/20">
                  <CardHeader>
                    <CardTitle className="text-base">Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <p>{generatedPlan.project_summary}</p>
                    <div className="space-y-2">
                      <Label htmlFor="schedule-start-date">Schedule start date</Label>
                      <Input id="schedule-start-date" type="date" value={scheduleStartDate}
                        onChange={(event) => setScheduleStartDate(event.target.value)} required />
                      <p className="text-muted-foreground">
                        Applying creates a sequential schedule in the listed task order, using Monday–Friday working days.
                        Weekend start dates move to Monday; holidays are not excluded. Each task starts after the previous task finishes. Phase dates
                        cover their tasks. This may take longer than the AI estimate. Existing work is unchanged.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div>
                        <p className="text-muted-foreground">Duration</p>
                        <p className="font-semibold">{generatedPlan.estimated_duration_days} days</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Cost Estimate</p>
                        <p className="font-semibold">
                          ${generatedPlan.estimated_cost_low?.toLocaleString()} - ${generatedPlan.estimated_cost_high?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Phases</p>
                        <p className="font-semibold">{generatedPlan.phases?.length || 0}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Activities</p>
                        <p className="font-semibold">{(generatedPlan.activities?.length || 0) +
                          (generatedPlan.phases || []).reduce((count, phase) => count + (phase.tasks?.length || 0), 0)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {generatedPlan.risk_flags?.length > 0 && (
                  <Card className="bg-yellow-500/10 border-yellow-500/30">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                        Risk Flags ({generatedPlan.risk_flags.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm">
                        {generatedPlan.risk_flags.slice(0, 3).map((risk, i) => (
                          <li key={i} className="flex gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              risk.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                              risk.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {risk.severity}
                            </span>
                            <span>{risk.description}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                <div className="text-xs text-muted-foreground text-center">
                  Tokens used: {generatedPlan.tokens_used?.toLocaleString()} / 10,000
                </div>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading state while fetching draft
  if (loadingDraft && isOpen) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[700px] bg-card border-border">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading your saved progress...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] bg-card border-border max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Project Plan Generator
            </DialogTitle>
            {/* Save status indicator */}
            <div className="flex items-center gap-2 text-xs">
              {saveStatus === 'saving' && (
                <span className="text-muted-foreground flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving...
                </span>
              )}
              {saveStatus === 'saved' && (
                <span className="text-emerald-400 flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  Saved
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="text-red-400 flex items-center gap-1">
                  <CloudOff className="h-3 w-3" />
                  Save failed
                </span>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            {STEPS.map((step, idx) => (
              <span
                key={step.id}
                className={idx <= currentStep ? "text-primary" : ""}
              >
                {step.title}
              </span>
            ))}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-4 min-h-[400px]">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>

          {currentStep < STEPS.length - 1 ? (
            <Button onClick={nextStep} className="bg-primary hover:bg-primary/90">
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : generatedPlan ? (
            <Button onClick={handleApplyPlan} disabled={!scheduleStartDate || applying} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="mr-2 h-4 w-4" />
              {applying ? "Applying..." : "Apply Plan to Project"}
            </Button>
          ) : (
            <Button
              onClick={handleGenerate}
              disabled={generating || !formData.scope_narrative}
              className="bg-primary hover:bg-primary/90"
            >
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating... (30-60s)
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate AI Plan
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScopeIntakeWizard;
